import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

import logo from "../../assets/logo.png";
import Stamp from "../../assets/stamp.png";
import PaidStamp from "../../assets/paidstamp.png";

const PURPLE = "#5B2DAD";
const LIGHT_BG = "#F3EEFB";

function formatDisplayDate(value) {
  if (!value) return "";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 32,
    paddingBottom: 120,
    paddingHorizontal: 32,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: "#111",
  },

  /* ---------- HEADER ---------- */

  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },

  title: {
    fontSize: 22,
    color: PURPLE,
    fontFamily: "Helvetica-Bold",
    marginBottom: 4,
  },

  meta: {
    fontSize: 9,
    fontFamily: "Helvetica-Oblique",
    color: "#444",
    marginBottom: 2,
  },

  logo: {
    width: 110,
    height: 40,
    objectFit: "contain",
  },

  /* ---------- BILLING ---------- */

  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 18,
  },

  box: {
    width: "48%",
    backgroundColor: LIGHT_BG,
    padding: 12,
    borderRadius: 6,
  },

  boxTitle: {
    color: PURPLE,
    fontFamily: "Helvetica-Bold",
    marginBottom: 6,
    fontSize: 10,
  },

  boxText: {
    fontSize: 9,
    marginBottom: 2,
  },

  /* ---------- TABLE ---------- */

  tableHeader: {
    flexDirection: "row",
    backgroundColor: PURPLE,
    color: "#fff",
    paddingVertical: 8,
    paddingHorizontal: 6,
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
  },

  tableRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderBottom: "1 solid #E5E7EB",
  },

  itemCol: {
    flex: 3,
  },

  col: {
    flex: 1,
    textAlign: "right",
    fontSize: 9,
  },

  description: {
    fontSize: 8.5,
    color: "#555",
    marginTop: 2,
    fontFamily: "Helvetica-Oblique",
  },

  discountText: {
    fontSize: 8,
    color: "#DC2626",
    marginTop: 2,
  },

  /* ---------- TOTALS ---------- */

  totals: {
    marginTop: 22,
    alignSelf: "flex-end",
    width: "42%",
  },

  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
    fontSize: 9,
  },

  grandTotal: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: "2 solid #000",
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
  },

  /* ---------- ADDITIONAL ---------- */

  additionalBox: {
    marginTop: 10,
    padding: 10,
  },

  additionalTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    color: PURPLE,
    marginBottom: 4,
  },

  additionalText: {
    fontSize: 9,
    marginBottom: 2,
    lineHeight: 1.35,
  },

  paymentSection: {
    marginTop: 18,
    borderWidth: 0.4,
    borderColor: PURPLE,
    borderStyle: "solid",
  },

  paymentHeader: {
    backgroundColor: PURPLE,
    paddingVertical: 7,
    paddingHorizontal: 10,
  },

  paymentHeaderText: {
    color: "#fff",
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
  },

  paymentRow: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: PURPLE,
    borderBottomStyle: "solid",
    backgroundColor: "#fff",
  },

  paymentRowAlt: {
    backgroundColor: LIGHT_BG,
  },

  paymentRowLast: {
    borderBottomWidth: 0,
  },

  paymentTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 6,
  },

  paymentDescription: {
    flex: 1,
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: "#111",
    paddingRight: 8,
  },

  paymentAmount: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    color: PURPLE,
  },

  paymentMetaRow: {
    flexDirection: "row",
  },

  paymentMetaItem: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 18,
  },

  paymentMetaLabel: {
    fontSize: 8,
    color: "#6B7280",
    marginRight: 4,
  },

  paymentMetaValue: {
    fontSize: 8,
    color: "#111",
    fontFamily: "Helvetica-Bold",
  },

  /* ---------- PAID STAMP ---------- */

  paidStamp: {
    position: "absolute",
    right: 60,
    top: 420,
    width: 180,
    opacity: 0.25,
    transform: "rotate(-12deg)",
  },

  /* ---------- COMPANY STAMP ---------- */

  Stamp: {
    position: "absolute",
    top: 620,
    width: 180,
  },

  /* ---------- FOOTER ---------- */

footer: {
  position: "absolute",
  bottom: 24,
  left: 32,
  right: 32,
  paddingTop: 10,
  borderTop: "1 solid #E5E7EB",
  alignItems: "center",
  textAlign: "center",
},

footerCompany: {
  fontSize: 10,
  fontFamily: "Helvetica-Bold",
  color: "#9B7ED0",
  marginBottom: 3,
},

footerContact: {
  fontSize: 8.5,
  color: "#9B7ED0",
  marginBottom: 2,
},

footerAddress: {
  fontSize: 8.5,
  color: "#9B7ED0",
},
});

export default function PdfInvoice({
  documentType = "invoice",
  invoiceNo,
  prnNo,
  invoiceDate,
  customer,
  items,
  totals,
  payment,
  taxMode,
}) {
  /* ---------- DOCUMENT LABELS ---------- */

  const documentLabels = {
    invoice: "Invoice",
    proforma: "Proforma Invoice",
    receipt: "Receipt",
  };

  const documentTitle = documentLabels[documentType] || "Invoice";

  const numberLabel =
    documentType === "receipt"
      ? "Receipt No"
      : documentType === "proforma"
        ? "Proforma Invoice No"
        : "Invoice No";

  const paymentRows = (payment?.paymentDetails || []).filter(
    (item) => item.description || item.amount || item.date || item.reference,
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* ---------- HEADER ---------- */}

        <View style={styles.header}>
          <View>
            <Text style={styles.title}>{documentTitle}</Text>

            <Text style={styles.meta}>
              {numberLabel}: {invoiceNo}
            </Text>

            <Text style={styles.meta}>Date: {formatDisplayDate(invoiceDate)}</Text>

            {prnNo && <Text style={styles.meta}>PRN No: {prnNo}</Text>}
          </View>

          <Image src={logo} style={styles.logo} />
        </View>

        {/* ---------- BILLING ---------- */}

        <View style={styles.row}>
          {/* COMPANY */}

          <View style={styles.box}>
            <Text style={styles.boxTitle}>Billed By</Text>

            <Text style={styles.boxText}>SPORTSNTW PRIVATE LIMITED</Text>

            <Text style={styles.boxText}>Unit No. 001, BPTP Park Centra</Text>

            <Text style={styles.boxText}>Gurugram, Haryana - 122001</Text>

            <Text style={styles.boxText}>PAN: AAZCS7833F</Text>

            <Text style={styles.boxText}>GSTIN: 06AAZCS7833F1ZQ</Text>
          </View>

          {/* CUSTOMER */}

          <View style={styles.box}>
            <Text style={styles.boxTitle}>
              {documentType === "receipt" ? "Received From" : "Billed To"}
            </Text>

            {customer.name ? (
              <Text style={styles.boxText}>{customer.name}</Text>
            ) : null}

            {customer.address ? (
              <Text style={styles.boxText}>{customer.address}</Text>
            ) : null}

            {customer.email ? (
              <Text style={styles.boxText}>Email: {customer.email}</Text>
            ) : null}

            {customer.phone ? (
              <Text style={styles.boxText}>Phone: {customer.phone}</Text>
            ) : null}

            {customer.pan ? (
              <Text style={styles.boxText}>PAN: {customer.pan}</Text>
            ) : null}

            {customer.gst ? (
              <Text style={styles.boxText}>GSTIN: {customer.gst}</Text>
            ) : null}
          </View>
        </View>

        {/* ---------- TABLE HEADER ---------- */}

        <View style={styles.tableHeader}>
          <Text style={styles.itemCol}>Item</Text>

          <Text style={styles.col}>Qty</Text>
          <Text style={styles.col}>HSN</Text>

          <Text style={styles.col}>Rate</Text>

          <Text style={styles.col}>Amount</Text>

          <Text style={styles.col}>GST</Text>

          <Text style={styles.col}>Total</Text>
        </View>

        {/* ---------- ITEMS ---------- */}

        {items.map((item, index) => {
          const discount = item.discount || 0;
          const taxable = item.taxableAmount;
          const taxValue = item.igst;
          const finalTotal = item.total;

          return (
            <View key={index} style={styles.tableRow}>
              {/* ITEM */}

              <View style={styles.itemCol}>
                <Text>{item.name}</Text>

                {item.description && (
                  <Text style={styles.description}>{item.description}</Text>
                )}

                {/* DISCOUNT */}

                {discount > 0 && (
                  <Text style={styles.discountText}>
                    Discount: Rs. {discount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                  </Text>
                )}
              </View>

              {/* QTY */}

              <Text style={styles.col}>{item.quantity}</Text>

              {/* HSN */}

              <Text style={styles.col}>9996</Text>

              {/* RATE */}

              <Text style={styles.col}>
                Rs. {Math.round(item.rate).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </Text>

              {/* TAXABLE */}

              <Text style={styles.col}>
                Rs. {taxable.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </Text>

              {/* GST */}

              <Text style={styles.col}>
                Rs. {taxValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </Text>

              {/* TOTAL */}

              <Text style={styles.col}>
                Rs. {finalTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </Text>
            </View>
          );
        })}

        {/* ---------- TOTALS ---------- */}

        <View style={styles.totals}>
          {/* AMOUNT */}

          <View style={styles.totalRow}>
            <Text>Amount</Text>

            <Text>Rs. {totals.amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</Text>
          </View>

          {/* DISCOUNT */}

          {totals.discount > 0 && (
            <View style={styles.totalRow}>
              <Text>Discount</Text>

              <Text>Rs. {totals.discount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</Text>
            </View>
          )}

          {/* TAXABLE */}

          <View style={styles.totalRow}>
            <Text>Taxable Amount</Text>

            <Text>Rs. {totals.taxableAmount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</Text>
          </View>

          {/* TAX */}

          {taxMode === "igst" ? (
            <View style={styles.totalRow}>
              <Text>IGST 18%</Text>

              <Text>Rs. {totals.igst.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</Text>
            </View>
          ) : (
            <>
              <View style={styles.totalRow}>
                <Text>CGST 9%</Text>

                <Text>Rs. {totals.cgst.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</Text>
              </View>

              <View style={styles.totalRow}>
                <Text>SGST 9%</Text>

                <Text>Rs. {totals.sgst.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</Text>
              </View>
            </>
          )}

          {/* GRAND TOTAL */}

          <View style={[styles.totalRow, styles.grandTotal]}>
            <Text>Total (INR)</Text>

            <Text>Rs. {totals.total.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</Text>
          </View>
        </View>

        {/* ---------- PAYMENT DETAILS ---------- */}

        {paymentRows.length > 0 && (
          <View style={styles.paymentSection}>
            <View style={styles.paymentHeader}>
              <Text style={styles.paymentHeaderText}>Payment Details</Text>
            </View>

            {paymentRows.map((item, i) => {
              const amountNumber = Number(
                String(item.amount || "").replace(/,/g, ""),
              );
              const amountText =
                item.amount && !Number.isNaN(amountNumber)
                  ? `Rs. ${amountNumber.toLocaleString("en-IN", {
                      maximumFractionDigits: 0,
                    })}`
                  : item.amount
                    ? `Rs. ${item.amount}`
                    : "-";

              const rowStyles = [styles.paymentRow];
              if (i % 2 === 1) rowStyles.push(styles.paymentRowAlt);
              if (i === paymentRows.length - 1) {
                rowStyles.push(styles.paymentRowLast);
              }

              const metaItems = [];
              if (item.date) {
                metaItems.push({
                  label: "DOP:",
                  value: formatDisplayDate(item.date),
                });
              }
              if (item.reference) {
                metaItems.push({
                  label: "RRN:",
                  value: item.reference,
                });
              }

              return (
                <View key={`payment-${i}`} style={rowStyles}>
                  <View style={styles.paymentTop}>
                    <Text style={styles.paymentDescription}>
                      {item.description || "Payment"}
                    </Text>
                    <Text style={styles.paymentAmount}>{amountText}</Text>
                  </View>

                  {metaItems.length > 0 && (
                    <View style={styles.paymentMetaRow}>
                      {metaItems.map((meta, metaIndex) => (
                        <View
                          key={`payment-meta-${i}-${metaIndex}`}
                          style={styles.paymentMetaItem}
                        >
                          <Text style={styles.paymentMetaLabel}>
                            {meta.label}
                          </Text>
                          <Text style={styles.paymentMetaValue}>
                            {meta.value}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}

        {/* ---------- ADDITIONAL INFO ---------- */}

        {payment.additionalInfo?.length > 0 ? (
          <View style={styles.additionalBox}>
            <Text style={styles.additionalTitle}>Additional Information</Text>

            {payment.additionalInfo.map((item, i) =>
              item.key && item.value ? (
                <Text key={i} style={styles.additionalText}>
                  {item.key}: {item.value}
                </Text>
              ) : null,
            )}
          </View>
        ) : null}

        {/* ---------- PAID STAMP ---------- */}

        {documentType === "receipt" && (
          <Image src={PaidStamp} style={styles.paidStamp} />
        )}

        {/* ---------- COMPANY STAMP ---------- */}

        <Image src={Stamp} style={styles.Stamp} />

        {/* ---------- FOOTER ---------- */}

{/* ---------- FOOTER ---------- */}

<View style={styles.footer} fixed>

  <Text style={styles.footerCompany}>
    SPORTSNTW PRIVATE LIMITED
  </Text>

  <Text style={styles.footerContact}>
    Accounts@barcaacademy.in | +91 9910506633
  </Text>

  <Text style={styles.footerAddress}>
    Unit No. 001, BPTP Park Centra, Gurugram, Haryana - 122001
  </Text>

</View>
      </Page>
    </Document>
  );
}
